import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Critical keywords in Spanish that indicate issues
const CRITICAL_KEYWORDS = [
  'problema',
  'falla',
  'queja',
  'insatisfecho',
  'deficiente',
  'mal',
  'pésimo',
  'terrible',
  'inaceptable',
  'demora',
  'retraso'
];

const LOW_RATING_THRESHOLD = 3; // 3 stars or less

export const useRealtimeFeedbackAlerts = () => {
  const [feedbackAlerts, setFeedbackAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);

  // Check if feedback contains critical keywords
  const hasCriticalKeywords = (comments) => {
    if (!comments) return false;
    const lowerComments = comments?.toLowerCase();
    return CRITICAL_KEYWORDS?.some(keyword => lowerComments?.includes(keyword));
  };

  // Determine alert priority based on rating and keywords
  const getAlertPriority = (rating, comments) => {
    const hasKeywords = hasCriticalKeywords(comments);
    
    if (rating === 1 || (rating === 2 && hasKeywords)) {
      return 'critical';
    } else if (rating <= LOW_RATING_THRESHOLD || hasKeywords) {
      return 'warning';
    }
    return 'info';
  };

  // Create alert object from feedback
  const createFeedbackAlert = (feedback) => {
    const rating = feedback?.satisfaction_rating;
    const comments = feedback?.service_comments;
    const hasKeywords = hasCriticalKeywords(comments);
    const priority = getAlertPriority(rating, comments);

    let message = '';
    if (rating <= LOW_RATING_THRESHOLD && hasKeywords) {
      message = `⚠️ Alerta crítica: Calificación baja (${rating}⭐) con comentarios negativos - Folio: ${feedback?.work_order?.folio_ot || 'N/A'}`;
    } else if (rating <= LOW_RATING_THRESHOLD) {
      message = `⚡ Calificación baja detectada (${rating}⭐) - Folio: ${feedback?.work_order?.folio_ot || 'N/A'}`;
    } else if (hasKeywords) {
      message = `⚠️ Comentarios críticos detectados - Folio: ${feedback?.work_order?.folio_ot || 'N/A'}`;
    }

    return {
      id: feedback?.id,
      type: 'customer_feedback',
      priority,
      message,
      rating,
      comments: comments?.substring(0, 100) + (comments?.length > 100 ? '...' : ''),
      workOrderFolio: feedback?.work_order?.folio_ot,
      clientName: feedback?.work_order?.client?.nombre,
      submittedAt: feedback?.submitted_at,
      hasKeywords
    };
  };

  // Fetch existing feedback alerts (low ratings or critical keywords)
  const fetchFeedbackAlerts = async () => {
    try {
      setIsLive(true);
      
      const { data, error: fetchError } = await supabase
        ?.from('customer_feedback')
        ?.select(`
          id,
          satisfaction_rating,
          service_comments,
          submitted_at,
          work_order:work_orders(
            folio_ot,
            client:clients(
              nombre
            )
          )
        `)
        ?.lte('satisfaction_rating', LOW_RATING_THRESHOLD)
        ?.order('submitted_at', { ascending: false })
        ?.limit(10);

      if (fetchError) {
        console.error('Error fetching feedback alerts:', fetchError);
        setError(fetchError?.message);
        return;
      }

      // Filter and create alerts
      const alerts = data
        ?.filter(feedback => {
          const hasLowRating = feedback?.satisfaction_rating <= LOW_RATING_THRESHOLD;
          const hasKeywords = hasCriticalKeywords(feedback?.service_comments);
          return hasLowRating || hasKeywords;
        })
        ?.map(createFeedbackAlert) || [];

      setFeedbackAlerts(alerts);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch feedback alerts:', err);
      setError(err?.message || 'Failed to fetch feedback alerts');
    } finally {
      setLoading(false);
      setIsLive(false);
    }
  };

  // Setup real-time subscription
  useEffect(() => {
    // Initial fetch
    fetchFeedbackAlerts();

    // Setup real-time subscription for new feedback
    const channel = supabase
      ?.channel('customer_feedback_alerts')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'customer_feedback'
        },
        async (payload) => {
          const newFeedback = payload?.new;
          
          // Check if this feedback should trigger an alert
          const hasLowRating = newFeedback?.satisfaction_rating <= LOW_RATING_THRESHOLD;
          const hasKeywords = hasCriticalKeywords(newFeedback?.service_comments);

          if (hasLowRating || hasKeywords) {
            // Fetch complete feedback with work order details
            const { data: completeFeedback } = await supabase
              ?.from('customer_feedback')
              ?.select(`
                id,
                satisfaction_rating,
                service_comments,
                submitted_at,
                work_order:work_orders(
                  folio_ot,
                  client:clients(
                    nombre
                  )
                )
              `)
              ?.eq('id', newFeedback?.id)
              ?.single();

            if (completeFeedback) {
              const newAlert = createFeedbackAlert(completeFeedback);
              setFeedbackAlerts(prev => [newAlert, ...prev]?.slice(0, 10));
            }
          }
        }
      )
      ?.subscribe();

    // Cleanup subscription
    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  return {
    feedbackAlerts,
    loading,
    error,
    isLive,
    refetch: fetchFeedbackAlerts
  };
};